import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Pending close timer for the hover-intent grace delay.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #closeTimer = null;

  /**
   * Whether the menu imagery has been eagerly loaded yet (deferred to first hover).
   * @type {boolean}
   */
  #imagesPreloaded = false;

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#handleKeyDown);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown);
    document.addEventListener('focusin', this.#handleDocumentFocusIn);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    document.removeEventListener('pointerdown', this.#handleDocumentPointerDown);
    document.removeEventListener('focusin', this.#handleDocumentFocusIn);
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Close an open mega menu when the pointer leaves the header/menu system.
   * @param {PointerEvent} event
   */
  #handleDocumentPointerDown = (event) => {
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#deactivate(this.#state.activeItem, true);
  };

  /**
   * Close an open mega menu when focus moves outside the header/menu system.
   * @param {FocusEvent} event
   */
  #handleDocumentFocusIn = (event) => {
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#deactivate(this.#state.activeItem, true);
  };

  /**
   * Keyboard handling for disclosure-style navigation.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (!this.#state.activeItem) return;

      event.preventDefault();
      const activeItem = this.#state.activeItem;
      this.#deactivate(activeItem, true);
      activeItem.focus();
      return;
    }

    if (event.key === 'ArrowDown' && event.target instanceof HTMLElement) {
      const item = findMenuItem(event.target);
      if (!item || item.getAttribute('aria-expanded') !== 'true') return;
      const submenu = findSubmenu(item);
      const firstLink = submenu?.querySelector('a[href], button:not([disabled]), summary');
      if (!(firstLink instanceof HTMLElement)) return;
      event.preventDefault();
      firstLink.focus();
      return;
    }

    if ((event.key === 'ArrowRight' || event.key === 'ArrowLeft') && event.target instanceof HTMLElement) {
      if (!event.target.matches('[ref="menuitem"]')) return;
      event.preventDefault();
      this.#focusSiblingMenuItem(event.target, event.key === 'ArrowRight' ? 1 : -1);
    }
  };

  /**
   * Move focus between visible top-level menu items.
   * @param {HTMLElement} currentItem
   * @param {1 | -1} direction
   */
  #focusSiblingMenuItem(currentItem, direction) {
    const items = Array.from(this.querySelectorAll('.menu-list__list-item:not([slot="overflow"]) > [ref="menuitem"]'))
      .filter((item) => item instanceof HTMLElement && item.offsetParent !== null);
    const currentIndex = items.indexOf(currentItem);
    if (currentIndex === -1 || items.length < 2) return;
    const nextIndex = (currentIndex + direction + items.length) % items.length;
    items[nextIndex]?.focus();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    // BB: cancel any pending close (hover-intent grace) so moving back into the
    // menu keeps it open, and lazily preload menu imagery on first interaction
    // rather than on every page load.
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
    if (!this.#imagesPreloaded) {
      this.#imagesPreloaded = true;
      this.#preloadImages();
    }

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const slottedHost = event.target.closest('[slot]');
    const slotName = slottedHost?.getAttribute('slot') || event.target.slot;
    const isOverflowItemInteraction = slotName === 'overflow';
    const isOverflowInteraction =
      slotName === 'more' || isOverflowItemInteraction;
    const isDefaultSlot = !isOverflowInteraction && event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (isOverflowItemInteraction) return;

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const isDropdownSubmenu = Boolean(submenu?.hasAttribute('data-menu-dropdown'));
    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    if (isDropdownSubmenu) {
      // Compact dropdowns are positioned independently and should not expand
      // Horizon's full-width submenu underlay.
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    const activeItemContainer = this.#state.activeItem?.parentElement;
    const isMovingWithinMenu = Boolean(
      relatedTarget && (menu?.contains(relatedTarget) || activeItemContainer?.contains(relatedTarget))
    );
    const isMovingToSubmenu =
      relatedTarget && event.type === 'blur' && menu?.contains(relatedTarget);
    const isMovingToOverflowMenu =
      relatedTarget instanceof Element &&
      (relatedTarget.matches('[part="overflow"], [part="overflow-list"]') ||
        Boolean(relatedTarget.closest('[slot="overflow"], [slot="more"]')));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // BB: defer the close on pointer exit instead of closing immediately. This
    // does three jobs at once:
    //  1. Dead-zone fix: the branded mega panel is absolutely positioned, with a
    //     strip of the header row between the trigger label and the panel content.
    //     Crossing that strip fires pointerleave on the <li>; without the delay the
    //     menu snapped shut before the cursor reached the panel. Re-entering the
    //     panel fires pointerenter -> activate, which cancels this timer, so the
    //     menu stays open through the transit.
    //  2. Hover-intent grace: clipping the panel edge no longer snaps it shut.
    //  3. Moving to the logo / search / account / cart icons (no re-entry) lets
    //     the timer run, so the menu closes as expected.
    // Keyboard (blur) still closes immediately for snappy a11y.
    if (event.type === 'pointerleave') {
      if (this.#closeTimer) clearTimeout(this.#closeTimer);
      this.#closeTimer = setTimeout(() => {
        this.#closeTimer = null;
        this.#deactivate();
      }, 240);
    } else {
      this.#deactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreItem = element.closest('[slot="more"]');
  if (moreItem instanceof Element) {
    return /** @type {HTMLElement | null} */ (moreItem.querySelector('[ref="menuitem"], .menu-list__link'));
  }

  const overflowItem = element.closest('[slot="overflow"]');
  if (overflowItem instanceof Element) {
    return /** @type {HTMLElement | null} */ (overflowItem.querySelector('[ref="menuitem"], .menu-list__link'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
